/**
 * SyncQueue - Централизованная очередь синхронизаций с приоритетами
 * 
 * Решает проблему race condition при параллельных синхронизациях:
 * - Все запросы синхронизации проходят через единую очередь
 * - HIGH priority (emergency sync) ждет завершения текущей синхронизации
 * - NORMAL priority имеет debounce (не чаще 5 секунд)
 * - Promise-based ожидание результатов
 */
import { apiClient } from './apiClient'
import { EmployeesRepo, type EmployeeFullData } from '../offline/employeesRepo'
import { LogsRepo } from '../offline/logsRepo'
import { db } from '../offline/db'
import { useDeviceStore } from '../store/deviceStore'
import { NetworkManager } from './networkManager'

export type SyncPriority = 'HIGH' | 'NORMAL'

interface SyncRequest {
  priority: SyncPriority
  resolve: (result: EmployeeFullData[]) => void
  reject: (error: Error) => void
  timestamp: number
}

interface SyncResult {
  data: EmployeeFullData[]
  etag: string | null
  modified: boolean
  fromCache: boolean
}

/**
 * Singleton класс для управления очередью синхронизаций
 */
class SyncQueueManager {
  private static instance: SyncQueueManager | null = null
  
  // Состояние синхронизации
  private isSyncing = false
  private currentSyncPromise: Promise<SyncResult> | null = null
  private pendingRequests: SyncRequest[] = []
  
  // ETag кеширование для incremental sync
  private cachedETag: string | null = null
  private readonly ETAG_STORAGE_KEY = 'syncqueue_etag'
  
  // Debounce для NORMAL приоритета
  private lastSyncTimestamp = 0
  private readonly DEBOUNCE_MS = 5000 // 5 секунд
  
  private constructor() {
    // Восстанавливаем ETag из localStorage при инициализации
    this.cachedETag = localStorage.getItem(this.ETAG_STORAGE_KEY)
    console.log('[SyncQueue] Initialized, restored ETag:', this.cachedETag || 'none')
  }
  
  /**
   * Получает singleton instance
   */
  static getInstance(): SyncQueueManager {
    if (!SyncQueueManager.instance) {
      SyncQueueManager.instance = new SyncQueueManager()
    }
    return SyncQueueManager.instance
  }
  
  /**
   * Запрашивает синхронизацию с указанным приоритетом
   * 
   * @param priority - 'HIGH' для emergency sync, 'NORMAL' для периодической
   * @returns Promise с массивом данных сотрудников
   */
  async requestSync(priority: SyncPriority = 'NORMAL'): Promise<EmployeeFullData[]> {
    const timestamp = Date.now()
    
    console.log(`[SyncQueue] 📥 Sync request received: priority=${priority}, isSyncing=${this.isSyncing}`)
    
    // Для NORMAL приоритета проверяем debounce
    if (priority === 'NORMAL' && this.shouldDebounce()) {
      console.log(`[SyncQueue] ⏸️ Debouncing NORMAL request (last sync ${Date.now() - this.lastSyncTimestamp}ms ago)`)
      // Возвращаем данные из кеша
      return await EmployeesRepo.getAllFullData()
    }
    
    // Если синхронизация уже идет - ждем её завершения
    if (this.isSyncing && this.currentSyncPromise) {
      console.log(`[SyncQueue] ⏳ Sync in progress, waiting for completion... (priority=${priority})`)
      
      // Для HIGH приоритета добавляем запрос в очередь ожидания
      if (priority === 'HIGH') {
        return new Promise<EmployeeFullData[]>((resolve, reject) => {
          this.pendingRequests.push({ priority, resolve, reject, timestamp })
          console.log(`[SyncQueue] 📝 Added HIGH priority request to queue (queue size: ${this.pendingRequests.length})`)
        })
      }
      
      // Для NORMAL приоритета просто ждем текущую синхронизацию
      try {
        const result = await this.currentSyncPromise
        return result.data
      } catch (error) {
        console.error('[SyncQueue] Error waiting for current sync:', error)
        return await EmployeesRepo.getAllFullData()
      }
    }
    
    // Запускаем новую синхронизацию
    return await this.executeSync()
  }
  
  /**
   * Проверяет, нужен ли debounce для NORMAL запросов
   */
  private shouldDebounce(): boolean {
    const timeSinceLastSync = Date.now() - this.lastSyncTimestamp
    return timeSinceLastSync < this.DEBOUNCE_MS
  }
  
  /**
   * Выполняет синхронизацию
   */
  private async executeSync(): Promise<EmployeeFullData[]> {
    this.isSyncing = true
    
    console.log('[SyncQueue] 🔄 Starting sync execution...')
    
    // Создаем Promise для текущей синхронизации
    this.currentSyncPromise = this.performSync()
    
    try {
      const result = await this.currentSyncPromise
      
      // Обновляем timestamp последней синхронизации
      this.lastSyncTimestamp = Date.now()
      
      // Резолвим все ожидающие запросы
      this.resolvePendingRequests(result.data)
      
      console.log(`[SyncQueue] ✅ Sync completed: ${result.data.length} employees, modified=${result.modified}, fromCache=${result.fromCache}`)
      
      return result.data
    } catch (error) {
      // Реджектим все ожидающие запросы
      this.rejectPendingRequests(error instanceof Error ? error : new Error(String(error)))
      
      console.error('[SyncQueue] ❌ Sync failed:', error)
      
      // Возвращаем данные из кеша при ошибке
      return await EmployeesRepo.getAllFullData()
    } finally {
      this.isSyncing = false
      this.currentSyncPromise = null
    }
  }
  
  /**
   * Выполняет фактическую синхронизацию с backend
   */
  private async performSync(): Promise<SyncResult> {
    const deviceStore = useDeviceStore.getState()
    
    if (!deviceStore.deviceId || !deviceStore.deviceSecret) {
      throw new Error('Device not registered')
    }
    
    const siteId = deviceStore.siteId
    if (!siteId) {
      throw new Error('Site ID not available')
    }
    
    // Проверяем сеть
    if (!NetworkManager.isOnline()) {
      const cached = await EmployeesRepo.getAllFullData()
      return { data: cached, etag: this.cachedETag, modified: false, fromCache: true }
    }

    // КРИТИЧНО: проверка ETag poisoning перед каждым запросом.
    // iOS/Android/Chrome могут очистить IndexedDB под давлением памяти,
    // НЕ трогая localStorage. В этом случае ETag есть, но кеш пуст.
    // Сервер вернёт 304 → мы прочитаем пустую базу → "сотрудник не найден".
    if (this.cachedETag) {
      const cacheCount = await db.employees_cache.count()
      if (cacheCount === 0) {
        console.warn('[SyncQueue] ⚠️ ETag poisoning detected (IDB empty, ETag set). Forcing full sync...')
        await LogsRepo.save('warn', 'ETag poisoning detected: IDB empty but ETag set, clearing ETag for full sync')
        this.clearETagCache()
      }
    }
    
    // Валидация UUID формата
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!uuidRegex.test(siteId)) {
      console.error(`[SyncQueue] ❌ Invalid site_id format: ${siteId}`)
      throw new Error(`Invalid site_id format: ${siteId}`)
    }
    
    // Устанавливаем credentials
    apiClient.setDeviceCredentials(deviceStore.deviceId, deviceStore.deviceSecret)
    
    const apiUrl = `/tablet/employees/full-data?site_id=${siteId}`
    console.log(`[SyncQueue] 📡 Requesting: ${apiUrl}`)
    console.log(`[SyncQueue] 📋 Cached ETag: ${this.cachedETag || 'none'}`)
    
    try {
      // Делаем запрос с ETag (если есть)
      const response = await this.fetchWithETag(apiUrl, this.cachedETag)
      
      if (response.notModified) {
        const cached = await EmployeesRepo.getAllFullData()
        
        // КРИТИЧНО: 304 с пустым кешем = ETag poisoning (IDB очистился после отправки запроса).
        // Сбрасываем ETag и немедленно делаем новый full-sync запрос.
        if (cached.length === 0) {
          console.warn('[SyncQueue] ⚠️ Got 304 but cache is empty — ETag poisoning! Retrying with full sync...')
          await LogsRepo.save('warn', 'Got 304 but IDB cache is empty (ETag poisoning), retrying full sync')
          this.clearETagCache()
          // Рекурсивно вызываем performSync без ETag → получим 200 с полными данными
          return this.performSync()
        }
        
        console.log(`[SyncQueue] 📦 304 Not Modified — ${cached.length} employees from cache`)
        return { data: cached, etag: this.cachedETag, modified: false, fromCache: true }
      }
      
      // Данные изменились - обновляем кеш
      const fullData = response.data
      const newETag = response.etag
      
      console.log(`[SyncQueue] ✅ Received ${fullData.length} employees from API`)
      
      // Сохраняем в IndexedDB с retry логикой
      await this.saveWithRetry(fullData)
      
      // Синхронизируем удаленных сотрудников
      const serverEmployeeIds = fullData.map(emp => emp.employee_id)
      const deletedCount = await EmployeesRepo.syncWithServer(serverEmployeeIds)
      if (deletedCount > 0) {
        console.log(`[SyncQueue] 🗑️ Removed ${deletedCount} deleted employees from cache`)
      }
      
      // Обновляем ETag и сохраняем в localStorage
      if (newETag) {
        this.cachedETag = newETag
        localStorage.setItem(this.ETAG_STORAGE_KEY, newETag)
        console.log(`[SyncQueue] 📋 Updated cached ETag: ${newETag}`)
      }
      
      await LogsRepo.save('info', `SyncQueue: fetched ${fullData.length} employees`, {
        site_id: siteId,
        count: fullData.length,
        etag: newETag || 'none',
        deleted_count: deletedCount
      })
      
      return {
        data: fullData,
        etag: newETag,
        modified: true,
        fromCache: false
      }
    } catch (error) {
      // Проверяем тип ошибки
      const isNetworkError = error instanceof Error && 
        (error.name === 'NetworkError' || 
         error.message.includes('Failed to fetch') ||
         error.message.includes('интернету'))
      
      if (isNetworkError) {
        console.log('[SyncQueue] ⏸️ Network error, returning cached data')
        const cached = await EmployeesRepo.getAllFullData()
        return {
          data: cached,
          etag: this.cachedETag,
          modified: false,
          fromCache: true
        }
      }
      
      throw error
    }
  }
  
  /**
   * Делает запрос к API с поддержкой ETag (If-None-Match)
   */
  private async fetchWithETag(
    url: string, 
    etag: string | null
  ): Promise<{ data: EmployeeFullData[], etag: string | null, notModified: boolean }> {
    const deviceStore = useDeviceStore.getState()
    
    // Формируем заголовки
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    
    // Добавляем ETag если есть
    if (etag) {
      headers['If-None-Match'] = etag
    }
    
    // Добавляем авторизацию через X-Device-ID и X-Device-Secret (как в apiClient)
    if (deviceStore.deviceId && deviceStore.deviceSecret) {
      headers['X-Device-ID'] = deviceStore.deviceId
      headers['X-Device-Secret'] = deviceStore.deviceSecret
    }
    
    const baseUrl = apiClient.getBaseUrl()
    const fullUrl = `${baseUrl}/api/v1${url}`
    
    console.log(`[SyncQueue] 🌐 Fetching: ${fullUrl}`)
    
    // Явный таймаут на fetch: SW предоставляет 10s через NetworkFirst,
    // но только если SW активен. Во время SW-обновления fetch идёт напрямую
    // без таймаута → может висеть минуты. Ставим 8s safety net.
    const controller = new AbortController()
    const fetchTimeoutId = setTimeout(() => {
      controller.abort(new Error('SyncQueue fetch timeout after 8000ms'))
    }, 8000)
    
    let response: Response
    try {
      response = await fetch(fullUrl, {
        method: 'GET',
        headers,
        signal: controller.signal,
      })
    } finally {
      clearTimeout(fetchTimeoutId)
    }
    
    // 304 Not Modified
    if (response.status === 304) {
      return {
        data: [],
        etag: etag,
        notModified: true
      }
    }
    
    // Проверяем успешность
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    // Получаем данные и ETag из ответа
    const data = await response.json() as EmployeeFullData[]
    const newETag = response.headers.get('ETag')
    
    return {
      data,
      etag: newETag,
      notModified: false
    }
  }
  
  /**
   * Сохраняет данные с retry логикой
   */
  private async saveWithRetry(employees: EmployeeFullData[], maxAttempts = 3): Promise<void> {
    let attempt = 0
    
    while (attempt < maxAttempts) {
      try {
        await EmployeesRepo.saveFullDataBatch(employees)
        console.log(`[SyncQueue] 💾 Saved ${employees.length} employees to cache`)
        return
      } catch (error) {
        attempt++
        console.error(`[SyncQueue] ❌ Save attempt ${attempt}/${maxAttempts} failed:`, error)
        
        if (attempt >= maxAttempts) {
          await LogsRepo.save('error', `CRITICAL: Failed to save employees after ${maxAttempts} attempts`, {
            error: error instanceof Error ? error.message : String(error),
            employees_count: employees.length
          })
          throw error
        }
        
        // Exponential backoff: 100ms, 200ms, 400ms
        const delay = 100 * Math.pow(2, attempt - 1)
        console.log(`[SyncQueue] ⏳ Retrying in ${delay}ms...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }
  
  /**
   * Резолвит все ожидающие запросы
   */
  private resolvePendingRequests(data: EmployeeFullData[]): void {
    if (this.pendingRequests.length === 0) return
    
    console.log(`[SyncQueue] ✅ Resolving ${this.pendingRequests.length} pending requests`)
    
    for (const request of this.pendingRequests) {
      request.resolve(data)
    }
    
    this.pendingRequests = []
  }
  
  /**
   * Реджектит все ожидающие запросы
   */
  private rejectPendingRequests(error: Error): void {
    if (this.pendingRequests.length === 0) return
    
    console.log(`[SyncQueue] ❌ Rejecting ${this.pendingRequests.length} pending requests`)
    
    for (const request of this.pendingRequests) {
      request.reject(error)
    }
    
    this.pendingRequests = []
  }
  
  /**
   * Сбрасывает кешированный ETag (используется при logout/перерегистрации)
   */
  clearETagCache(): void {
    this.cachedETag = null
    localStorage.removeItem(this.ETAG_STORAGE_KEY)
    console.log('[SyncQueue] 🗑️ ETag cache cleared from memory and localStorage')
  }
  
  /**
   * Получает текущий статус синхронизации
   */
  getStatus(): { isSyncing: boolean, pendingCount: number, lastSync: number, cachedETag: string | null } {
    return {
      isSyncing: this.isSyncing,
      pendingCount: this.pendingRequests.length,
      lastSync: this.lastSyncTimestamp,
      cachedETag: this.cachedETag
    }
  }
}

// Экспортируем singleton
export const SyncQueue = SyncQueueManager.getInstance()

// Экспортируем класс для тестирования
export { SyncQueueManager }
